/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-catch-shadow */
import { useQuery } from 'react-query';
import axios from 'axios';

import { apiConfig } from '@constants';

import { getDspAccessToken } from '../../helpers/DspAuth';
import { getThumbnailUrl, replaceTemplateValues } from '../../helpers/StringUtils';

export const getVODDetails = async (
  id: string | undefined,
  retry = false,
): Promise<string | FormattedData | VODDetailsError> => {
  const { accessToken, error } = await getDspAccessToken(retry);

  if (error) {
    return error;
  }

  const dataURL = replaceTemplateValues(apiConfig.VOD_SHOW_DETAILS_URL, { id });

  try {
    const { data } = await axios.get<RequestResponse>(dataURL, { headers: { 'x-access-token': accessToken } });

    return formatData(data, apiConfig.DSP_IMAGE_HOST_URL);
  } catch (error) {
    return retry ? { errors: 'access error. Token expired?' } : getVODDetails(id, true);
  }
};

const formatData = (data: RequestResponse, image_host: string): VODDetailsError | FormattedData => {
  if (!data.success) {
    return { errors: 'Failed' };
  }

  const vodData = data.data;

  const seasons = vodData.childchannels
    .sort((a, b) => a.season - b.season)
    .map(season => {
      const seasonID = season._id;
      const episodes = season.playlist.sort(sortPlaylists).map(episode => ({
        thumb: image_host + getThumbnailUrl(episode.thumb),
        slug: episode.slug,
        title: episode.title,
        description: episode.description,
        short_description: episode.short_description,
        id: episode._id,
        duration: episode.duration,
        year: episode.year,
        season: episode.season,
        episode: episode.episode,
        seriestitle: episode.seriestitle,
        seasonID,
      }));

      return {
        season: season.video.season,
        id: seasonID,
        episodes,
      };
    });

  return {
    id: vodData._id,
    videoID: vodData?.video?._id,
    title: vodData.title,
    description: vodData.description,
    short_description: vodData.short_description,
    language: vodData.language,
    year: vodData.year,
    rating: vodData.rating,
    releaseDate: vodData.or_release_date,
    poster: vodData.poster,
    wallpaper: vodData.wallpaper,
    slug: vodData.slug,
    seasons,
  };
};

const sortPlaylists = (a: Playlist, b: Playlist) => {
  if (a.episode && b.episode) {
    return a.episode - b.episode;
  }
  return 0;
};

type VODDetailsError = { errors: string };

type FormattedData = {
  id: string;
  videoID?: string;
  title: string;
  description: string;
  short_description?: string;
  language: string;
  year: string;
  rating: string;
  releaseDate?: string;
  poster: string;
  wallpaper: string;
  slug: string;
  seasons: {
    season: number;
    id: string;
    episodes: {
      thumb: string;
      slug: string;
      title: string;
      description: string;
      short_description: string;
      id: string;
      duration: string;
      year: string | undefined;
      season: number;
      episode: number | null;
      seriestitle: string;
      seasonID: string;
    }[];
  }[];
};

/**
 * Pass in id of video to get video url and details from DSP
 */
export const useGetVODShowDetails = (id: string | undefined) => useQuery(`getVODShow_${id}`, () => getVODDetails(id));

type RequestResponse = {
  success: boolean;
  data: RequestResponseData;
};

export type RequestResponseData = {
  _id: string;
  actors: string[];
  video?: Video; // NOT EXIST IN RESPONSE BUT EXIST IN "formatData" METHOD
  categories: Category[];
  childchannels: ChildChannel[];
  directors: string[];
  genres: string[];
  geo: string[];
  has_youtube: boolean;
  is_product: boolean;
  rating_reason: unknown[];
  story_blocks: unknown[];
  tags: string[];
  writers: string[];
  title: string;
  description: string;
  programming_type: string | 'Series';
  copyright?: string;
  tms_id?: string;
  type?: string;
  language: string | 'English';
  or_release_date?: string;
  year: string;
  rating: string;
  company_id: string;
  theme: Theme;
  channel_url: string;
  slug: string;
  poster: string;
  wallpaper: string;
  weightings: Record<string, number>[];
  custom_fields: CustomField[];
  country?: string | 'USA';
  dspro_id: string;
  spotlight_company_id: string;
  company: string;
  company_logo: string;
  trailer?: Trailer;
  short_description?: string;
};

type Category = {
  deleted: boolean;
  display_type: string | 'default';
  enabled: boolean;
  homepage: boolean;
  menu: boolean;
  movies_page: boolean;
  nested_categories: unknown[];
  nested_categories_full: unknown[];
  platforms: Platform[];
  poster: string;
  series_page: boolean;
  terms: unknown[];
  weight: number;
  wallpaper: string;
  _id: string;
  name: string;
  description: string;
  notes: string;
  status: string | 'published';
  type: string | 'manual';
  display_name: string;
  display_area: string;
  slug: string;
  custom_fields: CustomField[];
  curators_page: boolean;
  discuss_page: boolean;
  editorials_page: boolean;
  partners_page: boolean;
  read_page: boolean;
  themes_page: boolean;
  titles_page: boolean;
  watch_page: boolean;
};

type StringifyBoolean = 'true' | 'false';

type Platform = {
  amazon_fire: StringifyBoolean;
  andriod: StringifyBoolean;
  android_TV: StringifyBoolean;
  apple_tv: StringifyBoolean;
  html5: StringifyBoolean;
  ios: StringifyBoolean;
  roku: StringifyBoolean;
  'twc-legacy': StringifyBoolean;
  twcplustestdev: StringifyBoolean;
  tizen: StringifyBoolean;
  vizio: StringifyBoolean;
  website: StringifyBoolean;
  comcast_mrss: StringifyBoolean;
  comcast_ccx_mrss: StringifyBoolean;
  frequency_mrss: StringifyBoolean;
  nativo_mrss: StringifyBoolean;
  roku_channelstore: StringifyBoolean;
  roku_mrss: StringifyBoolean;
  vewd_mrss: StringifyBoolean;
  xumo_mrss: StringifyBoolean;
  dotstudiopro_mrss: StringifyBoolean;
};

type CustomField = {
  field_title: string | 'type';
  field_value: string | 'longform';
};

type ChildChannel = {
  title: string;
  description?: string;
  programming_type?: string | 'Series';
  language?: string | 'English';
  or_release_date?: string;
  year?: string;
  tags: (string | 'needsreview')[];
  rating?: string | 'TV-PG';
  company_id: string;
  theme: Theme;
  season: number;
  category: string;
  custom_fields?: CustomField[];
  video: Video;
  updated_at: string;
  channel_type: string | 'single';
  channel_url: string;
  slug: string;
  step: string | 'published';
  spotlight_poster?: string;
  wallpaper?: string;
  poster: string;
  actors?: unknown[];
  childchannels?: unknown[];
  childchannels_objectIds?: unknown[];
  directors?: unknown[];
  genres?: unknown[];
  playlist: Playlist[];
  rating_reason?: unknown[];
  videos_thumb: string;
  writers?: unknown[];
  include_in_iris_recommendations?: StringifyBoolean;
  weightings?: Record<string, number>[];
  dspro_id: string;
  playlist_id: string;
  _id: string;
  company: string;
  company_logo: string;
  template?: string;
  userflag?: StringifyBoolean;
  mrss_guid?: string;
  scheduled_publish_date: string;
  owner_id: string | number;
  mrss_images_feed?: MrssImagesFeed;
  videos_name: string;
  slug_updated?: StringifyBoolean;
  created_at: string;
  channelmeta_updated: StringifyBoolean;
  is_flag: StringifyBoolean;
  preview_url: string;
  user_flag?: StringifyBoolean;
  country?: string | 'USA';
  dsp_unpublish_date?: string;
  media_bucket?: string;
  media_bucket_name?: string;
  media_bucket_region?: string;
  published_button_click?: StringifyBoolean;
  dsp_publish_history?: string;
  channel_nextstep?: string;
  dsp_first_publish_date?: string;
  dsp_publish_date?: string;
  playlist_type: string | 'manual';
  geo: string[];
};

type Theme = {
  predefined: {
    showonroku?: null | unknown;
    remove_from_search: StringifyBoolean;
    remove_from_recommendation: StringifyBoolean;
  };
};

type Video = {
  _id: string;
  title: string;
  description: string;
  short_description: string;
  ads: Ads;
  geoblock: string;
  markets?: Markets;
  custom_fields: CustomField[];
  status: string | 'Uploaded';
  source: string;
  company_id: string;
  encoding_status: string | 'Finished';
  access: string | 'public';
  duration: number | string;
  paths: Paths;
  tags: unknown[];
  year: string;
  rating: string;
  language?: string | 'English';
  or_publish_date?: string;
  seriestitle: string;
  studio?: string;
  sunset_date?: string;
  category: string;
  season: number;
  episode: number;
  thumb: string;
  updated_at: string;
  created_at: string;
  slug: string;
  poster?: string;
  wallpaper?: string;
  thumbs: string[];
  frame_rate?: string | number;
  height?: number;
  width?: number;
  roku_bif_files: RokuBifFiles;
  closeCaption?: CloseCaption[];
  actors: unknown[];
  country?: string | 'USA';
  directors: unknown[];
  genres: string[];
  writers: unknown[];
  paywall?: Paywall;
  rating_reason?: unknown[];
  release_date?: string;
  teaser?: string | Teaser;
  bypass_channel_lock?: false;
  gracenote_id?: string;
  nielsendar?: string;
  current_version?: number;
};

type YesNo = 'no' | 'yes';

type Ads = {
  offsets?: {
    linear: unknown[];
  };
  over: YesNo;
  pre: YesNo;
  post: YesNo;
  mid: YesNo;
  pre_offset?: {
    pre_ads_number?: string;
    prechk?: StringifyBoolean;
    offsets?: {
      linear: string[];
    };
  };
  mid_offset?: {
    mid_ads_number: string[] | string | Record<string, string>;
    offsets: {
      linear: string[];
    };
  };
  post_offset?: {
    postchk: StringifyBoolean;
    offsets?: {
      linear: string[];
    };
  };
};

type Markets = {
  dma: {
    global?: boolean;
    allowed: unknown[];
  };
  sga?: {
    allowed: unknown[];
  };
};

type Paths = {
  thumbsDir: string;
  vodDir: string;
  source?: string;
  m3u8_url?: string;
};

type Playlist = {
  markets: Markets;
  mrss_images_feed: MrssImagesFeed;
  mrss_images_new: mMrssImagesNew;
  teaser?: Teaser | string;
  paths: Paths;
  ads: Ads;
  paywall?: Paywall;
  roku_bif_files: RokuBifFiles;
  tags: unknown[];
  thumbs: string[];
  youtube: unknown[];
  actors: unknown[];
  directors: unknown[];
  genres: unknown[];
  writers: unknown[];
  rating_reason: unknown[];
  server_side_ads: boolean;
  vmap: null | unknown;
  iab_category: unknown[];
  bypass_channel_lock: boolean;
  _id: string;
  mrss_guid?: string;
  title: string;
  description: string;
  short_description: string;
  geoblock: string;
  custom_fields: CustomField[];
  source: string;
  company_id: string;
  dashboard_upload?: StringifyBoolean;
  access: string | 'public';
  internal_syndication?: StringifyBoolean | boolean;
  origin_ids?: OriginIds;
  duration: string;
  searchable: boolean;
  year?: string;
  rating?: string | 'TV-PG';
  release_date?: string;
  language?: string | 'English';
  or_publish_date?: string;
  seriestitle: string;
  studio?: string | 'theweatherchannel';
  sunset_date?: string;
  category: string | 'Full Episode';
  ssai_provider?: string;
  season: number;
  episode: number | null;
  thumb: string;
  updated_at: string;
  created_at: string;
  slug: string;
  poster?: string;
  wallpaper?: string;
  audio_codec?: string | 'aac_lc';
  audio_sample_rate?: number;
  audio_sample_size?: number;
  frame_rate?: number;
  height?: number;
  pixel_aspect_ratio?: number;
  video_bitrate_kb?: number;
  video_codec?: string | 'h264';
  video_kind?: string | 'mp4';
  width?: number;
  file_size?: number;
  hybrik_machine_time?: number;
  newVideo?: StringifyBoolean | boolean;
  closeCaption?: CloseCaption[];
  generated_embedded_hls?: StringifyBoolean | boolean;
  media_bucket?: string;
  media_bucket_name?: string;
  media_bucket_region?: string;
  media_upload_bucket?: string;
  origin_company_name?: string;
  showonroku?: StringifyBoolean;
  country?: string | 'USA';
  closecaplanguage?: string;
  datePicker?: DatePicker;
  duration_hh?: string;
  duration_mm?: string;
  duration_ss?: string;
  embed?: Embed;
  geoCache?: StringifyBoolean;
  imdb?: string;
  links?: unknown[];
  orignal_file_name?: string;
  savebutton?: StringifyBoolean;
  video_upload_date?: string;
  video_upload_owner_id?: string | number;
  xmlfileurl?: string;
  video_upload_owner?: VideoUploadOwner;
  is_360_video?: boolean;
  gracenote_id?: string;
  nielsendar?: string;
  company?: unknown;
  current_version?: number;
  selectedFrame?: string;
};

type MrssImagesFeed = {
  channel_logo?: string;
  spotlight_poster?: string;
  thumbnails?: string[];
  poster?: string;
  wallpaper?: string;
  is_complete?: StringifyBoolean | boolean;
};

type mMrssImagesNew = {
  thumbnails: unknown[];
};

type Paywall = {
  rental_period: string | 'hours';
};

type RokuBifFiles = {
  sd: string;
  hd: string;
  fhd: string;
};

type CloseCaption = {
  srtPath: string;
  srtLanguage: string | 'English';
  srtFileName: string;
  uploaded?: string;
  srtExt: string;
  source?: string;
  originalFileName?: string;
};

type DatePicker = {
  date: {
    startDate: string;
    endDate: string;
  };
};

type Embed = {
  autostart: StringifyBoolean;
  disablesharing: StringifyBoolean;
  paywalldisable: StringifyBoolean;
  mute: StringifyBoolean;
  controls: StringifyBoolean;
  skin: StringifyBoolean;
};

type VideoUploadOwner = {
  first_name: string;
  last_name: string;
};

type Teaser = {
  _id: string;
  thumb: string;
  paths: string;
};

type OriginIds = {
  video_id: string;
  company_id: string;
};

type Trailer = {
  video_id: string;
  url: string;
};
